import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Sparkles, FlaskConical, Ear, Quote, ArrowRight, Utensils, Leaf, MessagesSquare, Layers, Wheat } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/06nmlr1f_croissants2.png';
const PROCESS_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/ca0sql9e_Danish.png';
const INNOVATION_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/2gicgvw7_bagel.png';

const PILLARS = [
  {
    Icon: Utensils,
    tag: '01 — No compromise on flavour',
    title: 'Flavour first, plant-based always.',
    body: "A plant-based pastry that isn't delicious isn't a pastry — it's an argument. We refuse to ship anything we wouldn't be proud to serve a Michelin critic or a hungry 19-year-old at 8:14 AM. Every recipe is tested blind against its dairy-and-egg equivalent, and only ships when our test kitchen agrees the plant-based version wins on flavour, texture and crumb.",
    bullets: [
      'Blind taste-tested against traditional recipes',
      'Cultured plant butters and oat-milk enrichments',
      'No artificial flavours, no shortcut emulsifiers'
    ]
  },
  {
    Icon: FlaskConical,
    tag: '02 — Ongoing food innovation',
    title: 'Always in the R&D kitchen.',
    body: "Our chefs are never satisfied. Every season, we release small-batch experiments — a new hydrocolloid ratio, an heirloom flour partner, a lamination we saw in Copenhagen — that eventually earn their place in the SKU lineup. Innovation isn't a project; it's the operating system.",
    bullets: [
      'Quarterly R&D cycles with named recipes',
      'Ingredient partnerships with Daiya, Yoso, TMRW',
      'Techniques imported from Copenhagen, Paris, Taiwan'
    ]
  },
  {
    Icon: Ear,
    tag: '03 — Listening to clients & customers',
    title: 'The best recipe starts with a question.',
    body: "Our best SKUs came from a conversation. A dining director mentioned students were skipping breakfast because nothing warm was ready by 8 AM — so we engineered an 18-minute bake-off pastry. A YMCA operator asked for something that could ride a hot line for two hours — the mac & cheese was born. We invite that conversation on every account.",
    bullets: [
      'Discovery interviews with every new operator',
      'Quarterly business reviews that shape the roadmap',
      'A "recipe request" line open to every client kitchen'
    ]
  }
];

const STANDARDS = [
  { Icon: Wheat, title: 'Ingredient-first sourcing', body: 'Heritage wheats, cultured plant butters, single-origin chocolate. Where an ingredient shows up on the label, it shows up on the mouth.' },
  { Icon: Layers, title: 'Hand-laminated pastry', body: 'Up to 81 paper-thin layers — folded by humans, never by extrusion machines.' },
  { Icon: MessagesSquare, title: 'A recipe request line', body: 'Every wholesale account gets a direct channel to our head chef. If it can be laminated, we\'ll prototype it.' }
];

export default function OurCraft() {
  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />

      {/* HERO */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-leaf-soft/40 blur-3xl pointer-events-none" />
        <div className="absolute top-20 -right-32 h-[26rem] w-[26rem] rounded-full bg-honey-soft/50 blur-3xl pointer-events-none" />

        <div className="container mx-auto relative">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-primary font-medium">Our Craft</span>
          </div>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="font-script text-2xl text-leaf">the philosophy</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary font-medium leading-[1.02] mt-2 text-balance">
                A pastry is a<br/>
                <span className="italic font-light text-leaf">promise kept.</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl text-pretty">
                Three commitments run through every recipe on our tray:
                <strong className="text-primary font-medium"> no compromise on flavour</strong>,
                <strong className="text-primary font-medium"> ongoing food innovation</strong>, and
                <strong className="text-primary font-medium"> listening carefully</strong> to the operators and eaters we serve.
                Everything else is technique.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                <Badge className="bg-secondary text-primary hover:bg-secondary rounded-full px-3 py-1"><Sparkles className="h-3 w-3 mr-1 text-leaf" /> Flavour first</Badge>
                <Badge className="bg-secondary text-primary hover:bg-secondary rounded-full px-3 py-1"><FlaskConical className="h-3 w-3 mr-1 text-leaf" /> Always innovating</Badge>
                <Badge className="bg-secondary text-primary hover:bg-secondary rounded-full px-3 py-1"><Ear className="h-3 w-3 mr-1 text-leaf" /> Listening deeply</Badge>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-[2rem] overflow-hidden shadow-pop aspect-[4/5]">
                <img src={HERO_IMG} alt="Trays of freshly-baked croissants" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto max-w-3xl text-center">
          <Quote className="h-6 w-6 text-leaf mx-auto" />
          <blockquote className="font-display text-2xl sm:text-3xl md:text-4xl text-primary italic font-light leading-[1.25] mt-5 text-balance">
            "If our plant-based version isn't more delicious than the original, we don't ship it. Full stop."
          </blockquote>
          <div className="mt-6 text-xs uppercase tracking-[0.2em] text-muted-foreground">— The Vegan Danish test kitchen</div>
        </div>
      </section>

      {/* THREE PILLARS — long editorial layout */}
      <section className="pb-8">
        <div className="container mx-auto space-y-24">
          {PILLARS.map(({ Icon, tag, title, body, bullets }, i) => {
            const imgs = [PROCESS_IMG, INNOVATION_IMG, HERO_IMG];
            const reversed = i % 2 === 1;
            return (
              <motion.div
                key={tag}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-12 gap-10 items-center ${reversed ? 'lg:[&>*:first-child]:order-2' : ''}`}
              >
                <div className="lg:col-span-5">
                  <div className="rounded-[2rem] overflow-hidden shadow-card aspect-[4/5]">
                    <img src={imgs[i]} alt={title} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-xs uppercase tracking-[0.18em] font-medium text-primary">
                    <Icon className="h-3.5 w-3.5 text-leaf" /> {tag}
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary font-medium leading-tight mt-4 text-balance">
                    {title}
                  </h2>
                  <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
                    {body}
                  </p>
                  <ul className="mt-6 space-y-2.5 max-w-lg">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-primary/90">
                        <span className="h-1.5 w-1.5 rounded-full bg-leaf mt-2 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* STANDARDS GRID */}
      <section className="py-24 md:py-32 mt-16 bg-parchment/40">
        <div className="container mx-auto">
          <div className="max-w-2xl">
            <div className="font-script text-2xl text-leaf">the standards</div>
            <h2 className="font-display text-4xl sm:text-5xl text-primary font-medium leading-tight mt-1">
              Non-negotiables<br/>
              <span className="italic font-light text-leaf">baked into every batch.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {STANDARDS.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="relative rounded-3xl bg-card border border-border/70 p-6 shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-500"
              >
                <div className="h-11 w-11 rounded-xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-semibold text-primary mt-4">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto">
          <div className="relative rounded-[2rem] gradient-leaf overflow-hidden shadow-pop">
            <div className="absolute inset-0 bg-noise opacity-[0.04]" />
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />

            <div className="relative grid lg:grid-cols-12 gap-8 p-8 sm:p-12 lg:p-14 items-center text-primary-foreground">
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-foreground/10 text-xs uppercase tracking-[0.18em]">
                  <Leaf className="h-3.5 w-3.5 text-accent" /> Have a recipe idea?
                </div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight text-balance">
                  We build best when we<br/>
                  <span className="italic font-light text-accent">build together.</span>
                </h3>
                <p className="mt-4 text-primary-foreground/85 max-w-xl">
                  If there's a plant-based pastry, meal or bowl your operator needs but can't find,
                  we'd love to hear about it. Most of our best-selling SKUs started as a client request.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-12">
                  <Link to="/wholesale#request">Send us your recipe idea <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 h-12 bg-transparent">
                  <Link to="/products">See the current line</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
